import React, { FC, useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  AlertTitle,
  LinearProgress,
  TableContainer,
  Paper,
} from "@mui/material";
import { Close, CloudUpload, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  FileImportPreviewResponseDto,
  UserProfileDto,
} from "@green0meter/api-client";
import { isEmpty } from "lodash";

import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@components/base";
import { useImportFromFiles } from "@api/hooks/ai-file-search";
import { useImportFormCsvData } from "@api";

interface AIImportDialogProps {
  open: boolean;
  onClose: () => void;
  formId: string;
  user: UserProfileDto | null;
  initialFiles?: File[];
}

export const AIImportDialog: FC<AIImportDialogProps> = ({
  open,
  onClose,
  formId,
  user,
  initialFiles,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewData, setPreviewData] =
    useState<FileImportPreviewResponseDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { importFromFilesAsync, isPendingImportFromFiles } =
    useImportFromFiles(user);
  const { importFormCsvDataAsync, isPendingImportFormCsvData } =
    useImportFormCsvData(user);

  useEffect(() => {
    if (open && initialFiles && !isEmpty(initialFiles)) {
      setSelectedFiles(initialFiles);
      setError(null);
      setPreviewData(null);
    }
  }, [open, initialFiles]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files || []);

    setSelectedFiles(files);
    setError(null);
    setPreviewData(null);
  };

  const handleUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleExtract = async (): Promise<void> => {
    if (selectedFiles.length === 0) return;

    setError(null);
    try {
      const result = await importFromFilesAsync({
        formId,
        files: selectedFiles,
      });

      setPreviewData(result);
    } catch (err) {
      const error = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };

      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to extract data from files",
      );
    }
  };

  const handleConfirmImport = async (): Promise<void> => {
    if (!previewData) return;

    try {
      await importFormCsvDataAsync({
        formId,
        csvData: previewData.extractedData,
      });
      handleClose();
    } catch (err) {
      const error = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };

      setError(
        error?.response?.data?.message || error?.message || "Failed to import data",
      );
    }
  };

  const handleClose = (): void => {
    setSelectedFiles([]);
    setPreviewData(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const isLoading = isPendingImportFromFiles || isPendingImportFormCsvData;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t("forms.aiImportTitle")}</Typography>
          {!isLoading && (
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t("common.somethingWrong")}</AlertTitle>
            {error}
          </Alert>
        )}

        {!previewData ? (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t("forms.aiImportDescription")}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">{t("forms.aiImportNote")}</Typography>
            </Alert>

            <input
              type="file"
              ref={fileInputRef}
              hidden
              multiple
              accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.rtf,.odt"
              onChange={handleFileSelect}
              disabled={isLoading}
            />

            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={handleUploadClick}
              disabled={isLoading}
              fullWidth
              sx={{ mb: 2 }}
            >
              {t("forms.selectFiles")}
            </Button>

            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("forms.selectedFiles")}:
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {t("common.fileName")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {t("documentHub.file.table.fileSize")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedFiles.map((file, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2">{file.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Alert
              severity={
                previewData.extractedQuestionsCount > 0 ? "success" : "warning"
              }
              sx={{ mb: 2 }}
            >
              <AlertTitle>{t("forms.extractionComplete")}</AlertTitle>
              <Typography variant="body2">
                {t("forms.extractedQuestionsCount", {
                  count: previewData.extractedQuestionsCount,
                  total: previewData.totalQuestionsInForm,
                })}
              </Typography>
            </Alert>

            {previewData.extractedData.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("forms.extractedData")}:
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 300,
                    overflow: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {t("common.questionSingular")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {t("forms.value")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.extractedData.slice(0, 20).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CheckCircle
                                fontSize="small"
                                color="success"
                                sx={{ flexShrink: 0 }}
                              />
                              <Typography variant="body2">
                                {item.questionEN}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.value}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!previewData ? (
          <>
            <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleExtract}
              disabled={selectedFiles.length === 0 || isLoading}
              isLoading={isPendingImportFromFiles}
            >
              {t("forms.extractData")}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                setPreviewData(null);
                setSelectedFiles([]);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
              disabled={isLoading}
            >
              {t("common.back")}
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={previewData.extractedData.length === 0 || isLoading}
              isLoading={isPendingImportFormCsvData}
            >
              {t("forms.confirmImport")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
